/**
 * Уборка базы.
 *
 * Приложение никогда ничего не удаляло физически. Мягко удалённая запись
 * остаётся в тех же индексных диапазонах, что и живая: она приезжает в каждую
 * выборку по датам, отфильтровывается уже в памяти и стоит ровно столько же,
 * сколько нужная. Вся стратегия «фильтруем `deletedAt` в JS» построена на том,
 * что удалённых мало, — а без уборки это предположение с годами перестаёт быть
 * верным. Правка курса лекарств, например, стирает будущие дозы десятками.
 *
 * Очередь синхронизации росла ещё быстрее: каждое действие добавляет строку, а
 * пока сервер не подключён, разбирать её некому. Через год ежедневного
 * пользования она становится крупнейшей таблицей базы, причём хранит копии
 * данных, которые и так лежат рядом.
 *
 * Обе задачи — фоновые. Они не должны ни блокировать запуск, ни удалять то, что
 * ещё может понадобиться синхронизации.
 */

import { db } from './database';
import type { OutboxRecord } from './schema';

/**
 * Сколько дней мягко удалённая запись ещё хранится.
 *
 * Срок не про интерфейс — отмена удаления живёт секунды, — а про синхронизацию:
 * пометка удаления должна успеть доехать до других устройств. Две недели
 * покрывают отпуск без сети.
 */
export const PURGE_AFTER_DAYS = 14;

/** Таблицы без мягкого удаления — их уборка не касается. */
const SYSTEM_TABLES = new Set(['users', 'outbox', 'settings']);

/**
 * Физически удаляет то, что было помечено удалённым давно.
 *
 * Записи, не доехавшие до сервера, не трогаются: несинхронизированное удаление
 * ещё не факт для остальных устройств.
 */
export async function purgeDeleted(
  now: number = Date.now(),
  afterDays: number = PURGE_AFTER_DAYS,
): Promise<number> {
  const threshold = now - afterDays * 24 * 60 * 60 * 1000;
  let removed = 0;

  for (const table of db.tables) {
    if (SYSTEM_TABLES.has(table.name)) continue;

    const stale = (await table.toArray()) as {
      id: string;
      deletedAt: number | null;
      syncState?: string;
    }[];

    const ids = stale
      .filter((row) => row.deletedAt !== null && row.deletedAt < threshold)
      .filter((row) => row.syncState !== 'pending' && row.syncState !== 'local')
      .map((row) => row.id);

    if (ids.length === 0) continue;

    await table.bulkDelete(ids);
    removed += ids.length;
  }

  return removed;
}

/**
 * Сжимает очередь изменений.
 *
 * Очередь — это журнал того, что нужно доставить, а не история правок. Десять
 * последовательных изменений одной задачи доставят на сервер ровно то же, что
 * и одно с итоговым состоянием, а сущность, созданная и удалённая до первой
 * синхронизации, серверу не нужна вовсе — её там никогда не было.
 *
 * Функция чистая: принимает записи очереди, возвращает, что оставить и что
 * убрать. Так поведение можно проверить, не трогая базу.
 */
export function compactOutbox(records: readonly OutboxRecord[]): {
  keep: OutboxRecord[];
  removeIds: string[];
} {
  const byEntity = new Map<string, OutboxRecord[]>();

  for (const record of records) {
    const key = `${record.entity}:${record.entityId}`;
    const group = byEntity.get(key);
    if (group) group.push(record);
    else byEntity.set(key, [record]);
  }

  const keep: OutboxRecord[] = [];
  const removeIds: string[] = [];

  for (const group of byEntity.values()) {
    const ordered = [...group].sort((a, b) => a.createdAt - b.createdAt);

    // Одна операция — сжимать нечего.
    if (ordered.length < 2) {
      keep.push(...ordered);
      continue;
    }

    const created = ordered.find((record) => record.operation === 'create');
    const deleted = ordered.findLast((record) => record.operation === 'delete');

    // Создано и удалено, не успев уехать: на сервере этой записи не было.
    if (created && deleted) {
      removeIds.push(...ordered.map((record) => record.id));
      continue;
    }

    if (deleted) {
      // Удаление отменяет всё, что было до него: править нечего.
      keep.push(deleted);
      removeIds.push(...ordered.filter((record) => record.id !== deleted.id).map((r) => r.id));
      continue;
    }

    // Иначе остаётся одна запись с итоговым состоянием. Патчи накладываются
    // по порядку: побеждает последнее значение каждого поля.
    const first = ordered[0];
    if (!first) continue;

    const payload = ordered.reduce<Record<string, unknown>>(
      (merged, record) => ({ ...merged, ...(record.payload as Record<string, unknown>) }),
      {},
    );

    // Базовая версия берётся у самой ранней правки: слитый патч накладывается
    // на то состояние, от которого человек отталкивался. Итоговая версия — у
    // последней, по ней сервер узнает повторную доставку.
    const last = ordered[ordered.length - 1] ?? first;

    keep.push({ ...first, payload, baseRevision: first.baseRevision, revision: last.revision });
    removeIds.push(...ordered.slice(1).map((record) => record.id));
  }

  return { keep, removeIds };
}

/** Как часто имеет смысл убираться. Чаще — трата батареи на пустом месте. */
const MAINTENANCE_INTERVAL_MS = 24 * 60 * 60 * 1000;

/** Ключ последней уборки. Первое применение таблицы настроек устройства. */
const LAST_RUN_KEY = 'maintenance.lastRun';

export interface MaintenanceReport {
  purged: number;
  compacted: number;
}

/**
 * Фоновая уборка: раз в сутки, после запуска.
 *
 * Возвращает `null`, если убираться пока рано. Ошибки наружу не выбрасываются:
 * уборка — не то, ради чего стоит показывать человеку экран сбоя.
 */
export async function runMaintenance(
  userId: string,
  now: number = Date.now(),
): Promise<MaintenanceReport | null> {
  try {
    const last = await db.settings.get(LAST_RUN_KEY);
    const lastRun = typeof last?.value === 'number' ? last.value : 0;
    if (now - lastRun < MAINTENANCE_INTERVAL_MS) return null;

    const purged = await purgeDeleted(now);
    const compacted = await compactOutboxTable(userId);

    await db.settings.put({ key: LAST_RUN_KEY, value: now, updatedAt: now });

    return { purged, compacted };
  } catch {
    // Уборка необязательна: не вышло сейчас — выйдет при следующем запуске.
    return null;
  }
}

/** Применяет сжатие очереди к базе. Возвращает, сколько записей убрано. */
export async function compactOutboxTable(userId: string): Promise<number> {
  return db.transaction('rw', db.outbox, async () => {
    const records = await db.outbox.where('userId').equals(userId).toArray();
    const { keep, removeIds } = compactOutbox(records);

    if (removeIds.length === 0) return 0;

    await db.outbox.bulkDelete(removeIds);
    // Записи со слитым состоянием переписываются: `keep` содержит их целиком.
    await db.outbox.bulkPut(keep.filter((record) => !removeIds.includes(record.id)));

    return removeIds.length;
  });
}
